import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { ArrowLeft, BookOpen, Search, Brain, Trash2, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';

const DeckDetail = () => {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    fetchDeck();
    fetchCards();
  }, [deckId, search, offset]);

  const fetchDeck = async () => {
    try {
      const response = await api.get(`/api/decks/${deckId}`);
      setDeck(response.data);
    } catch (error) {
      toast.error('Failed to load deck');
      navigate('/decks');
    }
  };

  const fetchCards = async () => {
    setLoading(true);
    try {
      const params = { deck_id: deckId, limit, skip: offset };
      if (search) params.search = search;
      const response = await api.get('/api/cards/', { params });
      setCards(response.data.cards || response.data || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error('Failed to load cards');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    fetchCards();
  };

  const handleStudyDeck = () => {
    navigate('/study', { state: { deckId } });
  };

  if (loading && !deck) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/decks')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{deck?.name}</h1>
            {deck?.description && (
              <p className="text-gray-600 mt-1">{deck.description}</p>
            )}
          </div>
        </div>
        <button
          onClick={handleStudyDeck}
          className="btn-primary flex items-center space-x-2"
        >
          <Brain className="w-5 h-5" />
          <span>Study This Deck</span>
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cards in this deck..."
            className="input-field pl-10"
          />
        </div>
      </form>

      {/* Cards Count */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {cards.length} of {totalCount} cards
      </div>

      {/* Cards List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : cards.length === 0 ? (
        <div className="card text-center py-16">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No cards in this deck</h3>
          <p className="text-gray-600 mb-6">Import vocabulary to add cards to this deck</p>
          <button onClick={() => navigate('/import')} className="btn-primary">
            Import Vocabulary
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((card) => (
            <div key={card.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-2xl font-bold text-gray-900">{card.word}</h3>
                {card.hsk_level && (
                  <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    HSK {card.hsk_level}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mb-2">{card.pinyin}</p>
              {card.meaning_en && (
                <p className="text-sm text-blue-600 mb-1">{card.meaning_en}</p>
              )}
              {card.meaning_vi && (
                <p className="text-sm text-emerald-600">{card.meaning_vi}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {cards.length > 0 && (
        <div className="flex justify-center gap-4 mt-8">
          <button
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-gray-600">
            Page {Math.floor(offset / limit) + 1}
          </span>
          <button
            onClick={() => setOffset(offset + limit)}
            disabled={cards.length < limit}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default DeckDetail;
